import { useEffect, useState } from "react";
import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  ShowButton,
  Pagination,
  SelectInput,
  Create,
  Edit,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextInput,
  NumberInput,
  BooleanInput,
  FormDataConsumer,
  useRecordContext,
  useNotify,
  required,
  minValue,
  maxValue,
  regex,
} from "react-admin";
import { Box, Chip, Typography, IconButton, Tooltip } from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import { referralProvider } from "../../dataProviders";

// ---------------------------------------------------------------------------
// Validation — mirrors CreateReferralCodeDto on the backend exactly. The
// backend is the source of truth (it re-validates everything regardless of
// what the client sends); this just gives the admin fast feedback.
// ---------------------------------------------------------------------------

const MAX_MARKUP_PERCENT = 7; // MAX_REFERRAL_MARKUP (0.07) expressed as a percent, matching the backend constant.

const validateCode = regex(/^[A-Z0-9]{4,12}$/, "4-12 uppercase letters/numbers");
const validateMarkupPercent = [required(), minValue(0), maxValue(MAX_MARKUP_PERCENT)];
const validateDurationValue = [required(), minValue(1), maxValue(365)];
const validateFixedDate = [
  required(),
  (value?: string) => {
    if (!value) return undefined;
    // Compare as plain date strings (UTC calendar days) — never construct a
    // Date from just a date string for comparison, that reintroduces the
    // browser-timezone trap the backend normalization is designed to avoid.
    const today = new Date().toISOString().slice(0, 10);
    return value < today ? "Must be today or later" : undefined;
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatExpirySummary = (record: any): string => {
  if (!record) return "";
  switch (record.expiryMode) {
    case "permanent":
      return "Lifetime";
    case "fixed_date":
      return record.fixedExpiryAt
        ? `Until ${new Date(record.fixedExpiryAt).toLocaleDateString(undefined, { timeZone: "UTC" })} (UTC)`
        : "Fixed date";
    case "duration":
      return `${record.durationValue} ${record.durationUnit} from signup`;
    default:
      return "—";
  }
};

// Generic copy-to-clipboard icon button — pass whichever field to copy
// (record.code or record.link) and the label to show in the toast/tooltip.
const CopyFieldButton = ({ field, what }: { field: "code" | "link"; what: string }) => {
  const record = useRecordContext();
  const notify = useNotify();
  const value = record?.[field];
  if (!value) return null;
  return (
    <Tooltip title={value}>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(value);
          notify(`${what} copied`, { type: "info" });
        }}
      >
        <ContentCopy fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const CopyCodeButton = () => <CopyFieldButton field="code" what="Referral code" />;
const CopyLinkButton = () => <CopyFieldButton field="link" what="Referral link" />;

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export const ReferralList = () => (
  <List
    filters={[
      <SelectInput
        key="status"
        source="status"
        choices={[
          { id: "active", name: "Active" },
          { id: "disabled", name: "Disabled" },
        ]}
        alwaysOn
      />,
    ]}
    title="Referral Codes"
    perPage={20}
    sort={{ field: "createdAt", order: "DESC" }}
    pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100]} />}
  >
    <Datagrid rowClick="show">
      <TextField source="code" />
      <CopyCodeButton />
      <TextField source="label" emptyText="—" />
      <FunctionField label="Markup" render={(r: any) => `${(Number(r.markupOverride) * 100).toFixed(2)}%`} />
      <FunctionField label="Expiry" render={formatExpirySummary} />
      <FunctionField
        label="Redemptions"
        render={(r: any) => `${r.redemptionCount}${r.maxRedemptions ? ` / ${r.maxRedemptions}` : ""}`}
      />
      <FunctionField
        label="Status"
        render={(r: any) => (
          <Chip
            label={r.status}
            size="small"
            color={r.status === "active" ? "success" : "default"}
            variant="outlined"
          />
        )}
      />
      <CopyLinkButton />
      <ShowButton />
    </Datagrid>
  </List>
);

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

// react-admin inputs work in the unit the admin thinks in (%); transform
// to/from the backend's decimal (0-1) at the form boundary only.
const percentToDecimal = (v: any) => (v === "" || v == null ? v : Number(v) / 100);
const decimalToPercent = (v: any) => (v === "" || v == null ? v : Number(v) * 100);

export const ReferralCreate = () => (
  <Create redirect="show">
    <SimpleForm sx={{ "& > .MuiFormControl-root, & > div": { mb: 3, display: "block" } }}>
      <TextInput
        source="code"
        label="Code"
        validate={validateCode}
        parse={(v: string) => v?.toUpperCase()}
        helperText="Optional — 4-12 uppercase letters/numbers, auto-generated if left blank"
        fullWidth
      />
      <TextInput source="label" label="Label / campaign name" fullWidth />
      <TextInput source="notes" label="Notes" multiline fullWidth />

      <NumberInput
        source="markupOverride"
        label="Markup (%)"
        validate={validateMarkupPercent}
        format={decimalToPercent}
        parse={percentToDecimal}
        helperText={`Must be below the system default (${MAX_MARKUP_PERCENT}%)`}
        fullWidth
      />

      <SelectInput
        source="expiryMode"
        label="Expiry"
        validate={required()}
        fullWidth
        choices={[
          { id: "permanent", name: "Lifetime (never expires)" },
          { id: "fixed_date", name: "Fixed date — same cutoff for everyone" },
          { id: "duration", name: "Duration — starts counting at signup" },
        ]}
      />

      <FormDataConsumer>
        {({ formData }) =>
          formData.expiryMode === "fixed_date" && (
            <TextInput
              source="fixedExpiryDate"
              label="Expiry date (UTC)"
              type="date"
              validate={validateFixedDate}
              helperText="A user who signs up on this date still gets the benefit for that full UTC day."
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          )
        }
      </FormDataConsumer>

      <FormDataConsumer>
        {({ formData }) =>
          formData.expiryMode === "duration" && (
            <Box sx={{ display: "flex", gap: 2 }}>
              <NumberInput source="durationValue" label="Duration" validate={validateDurationValue} />
              <SelectInput
                source="durationUnit"
                label="Unit"
                validate={required()}
                choices={[
                  { id: "days", name: "Days" },
                  { id: "months", name: "Months" },
                ]}
              />
            </Box>
          )
        }
      </FormDataConsumer>

      <NumberInput
        source="maxRedemptions"
        label="Max redemptions (blank = unlimited)"
        validate={[minValue(1)]}
      />
    </SimpleForm>
  </Create>
);

// ---------------------------------------------------------------------------
// Edit — only label/notes/status/maxRedemptions are changeable. To change the
// markup or expiry terms, disable this code and create a new one (matches
// the backend's UpdateReferralCodeDto, which silently drops any other field).
// ---------------------------------------------------------------------------

// A read-only value rendered with the same label-above-value shape as a real
// MUI input, so it doesn't collapse into the next field the way bare
// TextField/FunctionField text does with no label or margin of its own.
const ReadOnlyField = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
      {label}
    </Typography>
    <Typography variant="body1">{value}</Typography>
  </Box>
);

export const ReferralEdit = () => (
  <Edit>
    <SimpleForm sx={{ "& > .MuiFormControl-root, & > div": { mb: 3, display: "block" } }}>
      <FunctionField
        label=""
        render={(r: any) => (
          <Box>
            <ReadOnlyField label="Code" value={r.code} />
            <ReadOnlyField label="Markup (not editable — disable this code and create a new one to change it)" value={`${(Number(r.markupOverride) * 100).toFixed(2)}%`} />
            <ReadOnlyField label="Expiry (not editable — disable this code and create a new one to change it)" value={formatExpirySummary(r)} />
          </Box>
        )}
      />

      <TextInput source="label" label="Label / campaign name" fullWidth />
      <TextInput source="notes" label="Notes" multiline fullWidth />
      <BooleanInput
        source="status"
        label="Active"
        format={(v: string) => v === "active"}
        parse={(v: boolean) => (v ? "active" : "disabled")}
      />
      <NumberInput source="maxRedemptions" label="Max redemptions (blank = unlimited)" validate={[minValue(1)]} fullWidth />
    </SimpleForm>
  </Edit>
);

// ---------------------------------------------------------------------------
// Show — details + redemptions
// ---------------------------------------------------------------------------

const RedemptionsPanel = () => {
  const record = useRecordContext();
  const [redemptions, setRedemptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!record?.id) return;
    (referralProvider.getRedemptions(record.id) as Promise<any>)
      .then((res: any) => setRedemptions(res.data || []))
      .finally(() => setLoading(false));
  }, [record?.id]);

  if (loading) return <Typography variant="body2">Loading redemptions…</Typography>;
  if (!redemptions.length) return <Typography variant="body2" color="text.secondary">No redemptions yet.</Typography>;

  return (
    <Box>
      {redemptions.map((r) => (
        <Box key={r.id} sx={{ py: 1, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
          <Typography variant="body2">
            userId: {r.userId} · markup: {(Number(r.markupApplied) * 100).toFixed(2)}% · expires:{" "}
            {r.benefitExpiresAt ? new Date(r.benefitExpiresAt).toLocaleString() : "never"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            redeemed {new Date(r.redeemedAt).toLocaleString()}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export const ReferralShow = () => (
  <Show>
    <SimpleShowLayout>
      <FunctionField
        label="Code"
        render={(r: any) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">{r.code}</Typography>
            <CopyCodeButton />
          </Box>
        )}
      />
      <TextField source="label" emptyText="—" />
      <TextField source="notes" emptyText="—" />
      <FunctionField label="Markup" render={(r: any) => `${(Number(r.markupOverride) * 100).toFixed(2)}%`} />
      <FunctionField label="Expiry" render={formatExpirySummary} />
      <FunctionField
        label="Redemptions"
        render={(r: any) => `${r.redemptionCount}${r.maxRedemptions ? ` / ${r.maxRedemptions}` : " (unlimited)"}`}
      />
      <TextField source="status" />
      <FunctionField
        label="Signup link"
        render={(r: any) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">{r.link}</Typography>
            <CopyLinkButton />
          </Box>
        )}
      />
      <FunctionField label="Redemption history" render={() => <RedemptionsPanel />} />
    </SimpleShowLayout>
  </Show>
);
